import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loading } from '@cotera/client/app/components';
import { toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

/**
 * Creates a warehouse connection, for every surface that needs to prompt for
 * one: the settings panel and the "add a warehouse dataset" flow. Chrome-free —
 * the caller supplies the surrounding container — so the same fields and the
 * same `warehouse/connections` call back both places.
 *
 * The field lists per warehouse type mirror `Parser.Credentials` in
 * `libs/era/src/lib/parser/credentials.d.ts`, which is what the endpoint
 * validates against — including Snowflake's two auth shapes (password or an
 * RSA key pair).
 */

type WarehouseType = 'SNOWFLAKE' | 'POSTGRES' | 'REDSHIFT' | 'BIGQUERY';
type SnowflakeAuth = 'password' | 'keypair';

const WAREHOUSE_LABELS: Record<WarehouseType, string> = {
  SNOWFLAKE: 'Snowflake',
  POSTGRES: 'Postgres',
  REDSHIFT: 'Redshift',
  BIGQUERY: 'BigQuery',
};

type FieldKey =
  | 'account_id'
  | 'user'
  | 'password'
  | 'warehouse'
  | 'database'
  | 'host'
  | 'port'
  | 'read_schema'
  | 'write_schema'
  | 'role'
  | 'private_key'
  | 'public_key'
  | 'passphrase';

type FieldDef = {
  key: FieldKey;
  label: string;
  kind?: 'text' | 'password' | 'number';
  optional?: boolean;
  placeholder?: string;
};

/** Snowflake connection fields, minus the auth block (rendered separately). */
const SNOWFLAKE_FIELDS: FieldDef[] = [
  { key: 'account_id', label: 'Account', placeholder: 'xy12345.us-east-1' },
  { key: 'user', label: 'User' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'database', label: 'Database' },
  { key: 'read_schema', label: 'Read schema' },
  { key: 'write_schema', label: 'Write schema' },
  { key: 'role', label: 'Role', optional: true },
];

/** Host-based warehouses, whose whole credential set is plain fields. */
const SIMPLE_FIELDS: Record<'POSTGRES' | 'REDSHIFT', FieldDef[]> = {
  POSTGRES: [
    { key: 'host', label: 'Host', placeholder: 'localhost' },
    { key: 'port', label: 'Port', kind: 'number', placeholder: '5432' },
    { key: 'user', label: 'User' },
    { key: 'password', label: 'Password', kind: 'password', optional: true },
    { key: 'database', label: 'Database' },
    { key: 'read_schema', label: 'Read schema' },
    { key: 'write_schema', label: 'Write schema' },
  ],
  REDSHIFT: [
    { key: 'host', label: 'Host' },
    { key: 'port', label: 'Port', kind: 'number', placeholder: '5439' },
    { key: 'user', label: 'User' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'database', label: 'Database' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'read_schema', label: 'Read schema' },
    { key: 'write_schema', label: 'Write schema' },
  ],
};

const emptyFields = (): Record<FieldKey, string> => ({
  account_id: '',
  user: '',
  password: '',
  warehouse: '',
  database: '',
  host: '',
  port: '',
  read_schema: '',
  write_schema: '',
  role: '',
  private_key: '',
  public_key: '',
  passphrase: '',
});

/** The credentials payload the endpoint expects for the chosen type. */
const buildCredentials = (
  type: WarehouseType,
  snowflakeAuth: SnowflakeAuth,
  fields: Record<FieldKey, string>,
  bigQueryKeyFile: string
): { ok: true; value: unknown } | { ok: false; error: string } => {
  const pick = (key: FieldKey): string => fields[key].trim();
  switch (type) {
    case 'SNOWFLAKE': {
      const auth =
        snowflakeAuth === 'password'
          ? { password: fields.password }
          : {
              private_key: fields.private_key,
              public_key: fields.public_key,
              ...(pick('passphrase') !== '' && {
                passphrase: fields.passphrase,
              }),
            };
      return {
        ok: true,
        value: {
          type,
          credentials: {
            account_id: pick('account_id'),
            user: pick('user'),
            warehouse: pick('warehouse'),
            database: pick('database'),
            read_schema: pick('read_schema'),
            write_schema: pick('write_schema'),
            ...(pick('role') !== '' && { role: pick('role') }),
            ...auth,
          },
        },
      };
    }
    case 'POSTGRES':
      return {
        ok: true,
        value: {
          type,
          credentials: {
            user: pick('user'),
            password: fields.password,
            host: pick('host'),
            port: pick('port'),
            database: pick('database'),
            read_schema: pick('read_schema'),
            write_schema: pick('write_schema'),
          },
        },
      };
    case 'REDSHIFT':
      return {
        ok: true,
        value: {
          type,
          credentials: {
            user: pick('user'),
            password: fields.password,
            host: pick('host'),
            port: pick('port'),
            database: pick('database'),
            warehouse: pick('warehouse'),
            read_schema: pick('read_schema'),
            write_schema: pick('write_schema'),
          },
        },
      };
    case 'BIGQUERY': {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(bigQueryKeyFile) as Record<string, unknown>;
      } catch {
        return {
          ok: false,
          error: 'The service-account key must be valid JSON.',
        };
      }
      return {
        ok: true,
        value: {
          type,
          credentials: {
            ...parsed,
            read_schema: pick('read_schema'),
            write_schema: pick('write_schema'),
          },
        },
      };
    }
  }
};

const defaultDisplayName = (
  type: WarehouseType,
  fields: Record<FieldKey, string>
): string => {
  switch (type) {
    case 'SNOWFLAKE':
      return fields.account_id.trim() || 'Snowflake';
    case 'POSTGRES':
    case 'REDSHIFT': {
      const db = fields.database.trim();
      const host = fields.host.trim();
      return db !== '' && host !== ''
        ? `${db}@${host}`
        : WAREHOUSE_LABELS[type];
    }
    case 'BIGQUERY':
      return 'BigQuery';
  }
};

export const ConnectWarehouseForm: React.FC<{
  /** Called after the connection is created and the primary-connection cache is invalidated. */
  onConnected: () => void;
  submitLabel?: string;
}> = ({ onConnected, submitLabel = 'Connect warehouse' }) => {
  const org = useWhoami((s) => s.org);
  const client = useTenantedClient();
  const queryClient = useQueryClient();

  const [type, setType] = useState<WarehouseType>('SNOWFLAKE');
  const [snowflakeAuth, setSnowflakeAuth] = useState<SnowflakeAuth>('password');
  const [displayName, setDisplayName] = useState('');
  const [fields, setFields] = useState<Record<FieldKey, string>>(emptyFields());
  const [bigQueryKeyFile, setBigQueryKeyFile] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key: FieldKey) => (value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const complete = ((): boolean => {
    if (type === 'BIGQUERY') {
      return (
        bigQueryKeyFile.trim() !== '' &&
        fields.read_schema.trim() !== '' &&
        fields.write_schema.trim() !== ''
      );
    }
    if (type === 'SNOWFLAKE') {
      const baseComplete = SNOWFLAKE_FIELDS.every(
        (field) => field.optional === true || fields[field.key].trim() !== ''
      );
      const authComplete =
        snowflakeAuth === 'password'
          ? fields.password.trim() !== ''
          : fields.private_key.trim() !== '' && fields.public_key.trim() !== '';
      return baseComplete && authComplete;
    }
    return SIMPLE_FIELDS[type].every(
      (field) => field.optional === true || fields[field.key].trim() !== ''
    );
  })();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!complete || saving) {
      return;
    }
    const credentials = buildCredentials(
      type,
      snowflakeAuth,
      fields,
      bigQueryKeyFile
    );
    if (!credentials.ok) {
      toast.error(credentials.error);
      return;
    }
    setSaving(true);
    const name = displayName.trim();
    const result = await client.warehouse.connection.create({
      displayName: name === '' ? defaultDisplayName(type, fields) : name,
      skipSetup: false,
      credentials: credentials.value,
    });
    setSaving(false);
    if (result.isErr()) {
      toast.error(
        result.error.err.message ?? 'Could not connect the warehouse.'
      );
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.warehouse.getPrimary({ orgId: org.id }),
    });
    toast.success('Warehouse connected.');
    onConnected();
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <Field label="Warehouse type">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as WarehouseType)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          {(Object.keys(WAREHOUSE_LABELS) as WarehouseType[]).map((option) => (
            <option key={option} value={option}>
              {WAREHOUSE_LABELS[option]}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Connection name (optional)">
        <Input
          value={displayName}
          placeholder={defaultDisplayName(type, fields)}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </Field>

      {type === 'BIGQUERY' ? (
        <>
          <Field label="Service account key (JSON)">
            <Textarea
              value={bigQueryKeyFile}
              rows={6}
              spellCheck={false}
              className="font-mono text-xs"
              placeholder='{ "type": "service_account", "project_id": "...", ... }'
              onChange={(e) => setBigQueryKeyFile(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Read schema">
              <Input
                value={fields.read_schema}
                onChange={(e) => set('read_schema')(e.target.value)}
              />
            </Field>
            <Field label="Write schema">
              <Input
                value={fields.write_schema}
                onChange={(e) => set('write_schema')(e.target.value)}
              />
            </Field>
          </div>
        </>
      ) : type === 'SNOWFLAKE' ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            {SNOWFLAKE_FIELDS.map((field) => (
              <FieldFromDef
                key={field.key}
                field={field}
                value={fields[field.key]}
                onChange={set(field.key)}
              />
            ))}
          </div>
          <Field label="Authentication">
            <select
              value={snowflakeAuth}
              onChange={(e) =>
                setSnowflakeAuth(e.target.value as SnowflakeAuth)
              }
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="password">Password</option>
              <option value="keypair">Key pair</option>
            </select>
          </Field>
          {snowflakeAuth === 'password' ? (
            <Field label="Password">
              <Input
                type="password"
                value={fields.password}
                onChange={(e) => set('password')(e.target.value)}
              />
            </Field>
          ) : (
            <>
              <Field label="Private key">
                <Textarea
                  value={fields.private_key}
                  rows={5}
                  spellCheck={false}
                  className="font-mono text-xs"
                  placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                  onChange={(e) => set('private_key')(e.target.value)}
                />
              </Field>
              <Field label="Public key">
                <Textarea
                  value={fields.public_key}
                  rows={4}
                  spellCheck={false}
                  className="font-mono text-xs"
                  placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
                  onChange={(e) => set('public_key')(e.target.value)}
                />
              </Field>
              <Field label="Passphrase (optional)">
                <Input
                  type="password"
                  value={fields.passphrase}
                  onChange={(e) => set('passphrase')(e.target.value)}
                />
              </Field>
            </>
          )}
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {SIMPLE_FIELDS[type].map((field) => (
            <FieldFromDef
              key={field.key}
              field={field}
              value={fields[field.key]}
              onChange={set(field.key)}
            />
          ))}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button type="submit" disabled={!complete || saving}>
          {saving ? <Loading.Spinner variant="sm" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

const FieldFromDef: React.FC<{
  field: FieldDef;
  value: string;
  onChange: (value: string) => void;
}> = ({ field, value, onChange }) => (
  <Field
    label={field.optional === true ? `${field.label} (optional)` : field.label}
  >
    <Input
      type={
        field.kind === 'password'
          ? 'password'
          : field.kind === 'number'
          ? 'number'
          : 'text'
      }
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <label className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);
